import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Clock, Shield, FileText, Users, ArrowRight, BookOpen, AlertCircle, Camera } from "lucide-react";
import SetmoreBooking from "@/components/SetmoreBooking";
import SEO from "@/components/SEO";
import drugTestCollection from "@/assets/drug-test-collection.jpg";
import drugTestLab from "@/assets/drug-test-lab.jpg";
import dotDriverCompliance from "@/assets/dot-driver-compliance.jpg";
import drugTestResults from "@/assets/drug-test-results.jpg";
import ccfFormBg from "@/assets/ccf-form-bg.jpg";

const galleryImages = [
  { src: drugTestCollection, alt: "DOT drug test specimen collection cup with chain of custody form" },
  { src: drugTestLab, alt: "Certified collector processing drug test specimen in laboratory" },
  { src: dotDriverCompliance, alt: "CDL truck driver reviewing DOT drug test compliance documents" },
  { src: drugTestResults, alt: "Drug test results report and documentation" },
];

const dotPolicySections = [
  {
    title: "How the collection works",
    icon: <BookOpen className="h-5 w-5 text-blue-600" />,
    source: "49 CFR Part 40, Subparts C–F",
    points: [
      "Urine specimen is collected by a trained Collector at a designated collection site",
      "Specimen is split into Bottle A (primary) and Bottle B (split) for verification if needed",
      "Sealed specimens are sent to a SAMHSA-certified laboratory for analysis",
      "A licensed Medical Review Officer (MRO) reviews all laboratory results before reporting",
      "Federal Chain of Custody and Control Form (CCF) documents every step of the process",
    ],
  },
  {
    title: "5 drug categories tested (DOT mandated)",
    icon: <FileText className="h-5 w-5 text-blue-600" />,
    source: "49 CFR Part 40, Subpart F — DOT 5-Panel",
    points: [
      "Marijuana metabolites (THC) — federally prohibited regardless of state law",
      "Cocaine metabolites",
      "Amphetamines — including methamphetamine and MDMA (ecstasy)",
      "Opioids — codeine, morphine, heroin, oxycodone, oxymorphone, hydrocodone, hydromorphone",
      "Phencyclidine (PCP)",
    ],
  },
  {
    title: "When testing is required",
    icon: <AlertCircle className="h-5 w-5 text-blue-600" />,
    source: "49 CFR Part 382 (FMCSA) & agency-specific rules",
    points: [
      "Pre-employment — before an employee first performs a safety-sensitive function",
      "Random — unannounced, conducted throughout the calendar year at DOT-set rates",
      "Post-accident — following qualifying accidents as defined by the DOT agency",
      "Reasonable suspicion — when a trained supervisor observes specific signs of impairment",
      "Return-to-duty — required negative test before resuming safety-sensitive duties after a violation",
      "Follow-up — minimum 6 unannounced tests in the first 12 months after return-to-duty",
    ],
  },
  {
    title: "What happens after a violation",
    icon: <Shield className="h-5 w-5 text-blue-600" />,
    source: "49 CFR Part 40, Subpart O — Return-to-Duty Process",
    points: [
      "Employee is immediately removed from all safety-sensitive functions",
      "Employer must refer employee to a DOT-qualified Substance Abuse Professional (SAP)",
      "SAP conducts face-to-face evaluation and recommends education or treatment",
      "Employee must complete SAP's recommended program before a return-to-duty test",
      "Return-to-duty test must be directly observed and return a verified negative result",
    ],
  },
];

const DrugTesting = () => {
  return <>
    <SEO 
      title="DOT & Non-DOT Drug Testing | NearLab Services"
      description="Professional DOT and Non-DOT drug testing services. Federal compliance testing for transportation industry and flexible testing for employers. Fast, accurate results."
      keywords="DOT drug testing, non-DOT drug testing, workplace drug testing, CDL drug test, employer drug screening, drug testing near me"
      url="https://nearlabservices.com/drug-testing"
    />
    <div className="min-h-screen bg-background">

      {/* Hero Section */}
      <section className="relative py-24 px-4 overflow-hidden min-h-[70vh] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${ccfFormBg})` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--nearlab-teal-dark))/92] via-[hsl(var(--nearlab-teal))/80] to-[hsl(var(--nearlab-teal))/40]" aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto w-full">
          <div className="max-w-2xl">
            <div className="flex flex-wrap gap-2 mb-6">
              <span className="inline-flex items-center gap-1.5 bg-[hsl(var(--nearlab-gold))] text-[hsl(var(--nearlab-teal-dark))] px-3 py-1 rounded-full text-sm font-semibold shadow">
                <Shield className="h-3.5 w-3.5" /> DOT Compliant
              </span>
              <span className="inline-flex items-center bg-white/80 backdrop-blur-sm border border-black/20 text-black px-3 py-1 rounded-full text-sm font-semibold shadow">
                SAMHSA Certified Labs
              </span>
              <span className="inline-flex items-center bg-white/80 backdrop-blur-sm border border-black/20 text-black px-3 py-1 rounded-full text-sm font-semibold shadow">
                Federal CCF Forms
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight mb-6 drop-shadow-lg">
              DOT & Non-DOT{" "}
              <span className="text-[hsl(var(--nearlab-gold))]">Drug Testing</span>
            </h1>
            <p className="text-lg text-black mb-8 max-w-xl leading-relaxed font-medium">
              Whether you need a federally required DOT test or workplace screening, we make it 
              straightforward — no confusing paperwork, no long waits. Accurate results handled by people who know what they're doing.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="bg-[hsl(var(--nearlab-gold))] text-[hsl(var(--nearlab-teal-dark))] hover:bg-[hsl(var(--nearlab-gold-dark))] font-bold shadow-lg"
                onClick={() => window.location.href = '/booking'}
                aria-label="Schedule DOT drug testing appointment"
              >
                Schedule DOT Test
                <ArrowRight className="ml-2" size={18} />
              </Button>
              <Button 
                size="lg" 
                className="bg-white text-[hsl(var(--nearlab-teal-dark))] hover:bg-white/90 font-bold shadow-lg"
                onClick={() => window.location.href = '/booking'}
                aria-label="Schedule non-DOT drug testing appointment"
              >
                Schedule Non-DOT Test
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stat Bar */}
      <section className="bg-[hsl(var(--nearlab-teal-dark))] text-white py-6 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { value: "DOT", label: "Federally Compliant" },
              { value: "15 min", label: "Average Collection" },
              { value: "SAMHSA", label: "Certified Labs" },
              { value: "Same-Day", label: "Appointments" },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-2xl font-bold text-[hsl(var(--nearlab-gold))]">{stat.value}</div>
                <div className="text-sm text-white/70 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-14 px-4 bg-[hsl(var(--nearlab-teal-light))/30]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 border border-[hsl(var(--nearlab-teal))] text-[hsl(var(--nearlab-teal))] px-4 py-1.5 rounded-full text-sm font-medium mb-4">
              <Camera className="h-4 w-4" />
              A Look Inside
            </span>
            <h2 className="text-3xl font-bold text-[hsl(var(--nearlab-teal-dark))] mb-3">What to Expect When You Come In</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">Our collectors are trained to walk you through every step calmly and professionally — start to finish.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {galleryImages.map((img, i) => (
              <div key={i} className="relative rounded-xl overflow-hidden aspect-[4/3] shadow-md group">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <p className="absolute bottom-2 left-2 right-2 text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">{img.alt}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DOT vs Non-DOT */}
      <section className="py-16 px-4 bg-background">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[hsl(var(--nearlab-teal-dark))] mb-4">Which test do you need?</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              DOT tests are federally required for certain jobs — Non-DOT covers everything else.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {/* DOT Card */}
            <Card className="border-2 border-[hsl(var(--nearlab-teal))/30] shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="rounded-t-lg" style={{ background: "var(--gradient-teal)" }}>
                <div className="flex items-center gap-2">
                  <Shield className="h-6 w-6 text-[hsl(var(--nearlab-gold))]" />
                  <CardTitle className="text-2xl text-white">DOT Testing</CardTitle>
                </div>
                <CardDescription className="text-white/80">If your job is regulated by the federal government, this is the one you need</CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <div className="mb-6">
                  <h4 className="font-semibold text-[hsl(var(--nearlab-teal-dark))] mb-3">Who needs this:</h4>
                  <ul className="text-muted-foreground space-y-1">
                    <li>• CDL truck & bus drivers</li>
                    <li>• Pilots and airline crew</li>
                    <li>• Railroad employees</li>
                    <li>• Pipeline workers</li>
                    <li>• Maritime / Coast Guard workers</li>
                  </ul>
                </div>
                <div className="bg-[hsl(var(--nearlab-teal-light))/50] p-4 rounded-lg mb-4 border border-[hsl(var(--nearlab-teal))/20]">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="h-4 w-4 text-[hsl(var(--nearlab-teal))]" />
                    <span className="font-semibold text-[hsl(var(--nearlab-teal-dark))]">Starting at $70</span>
                  </div>
                  <p className="text-[hsl(var(--nearlab-teal))] text-sm">All required federal paperwork included</p>
                </div>
                <Button 
                  className="w-full bg-[hsl(var(--nearlab-teal))] hover:bg-[hsl(var(--nearlab-teal-dark))] text-white"
                  onClick={() => window.location.href = '/booking'}
                >
                  Schedule DOT Test
                </Button>
              </CardContent>
            </Card>

            {/* Non-DOT Card */}
            <Card className="border-2 border-[hsl(var(--nearlab-gold))/30] shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="rounded-t-lg bg-[hsl(var(--nearlab-teal-dark))]">
                <div className="flex items-center gap-2">
                  <Users className="h-6 w-6 text-[hsl(var(--nearlab-gold))]" />
                  <CardTitle className="text-2xl text-white">Non-DOT Testing</CardTitle>
                </div>
                <CardDescription className="text-white/80">For employers, personal testing, and everything outside of federal rules</CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <div className="mb-6">
                  <h4 className="font-semibold text-[hsl(var(--nearlab-teal-dark))] mb-3">Common reasons people come in:</h4>
                  <ul className="text-muted-foreground space-y-1">
                    <li>• Hiring a new employee</li>
                    <li>• Random workplace testing programs</li>
                    <li>• After a workplace accident</li>
                    <li>• Personal peace of mind</li>
                    <li>• Court-ordered testing</li>
                  </ul>
                </div>
                <div className="bg-[hsl(var(--nearlab-gold-light))] p-4 rounded-lg mb-4 border border-[hsl(var(--nearlab-gold))/20]">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="h-4 w-4 text-[hsl(var(--nearlab-gold-dark))]" />
                    <span className="font-semibold text-[hsl(var(--nearlab-teal-dark))]">Starting at $60</span>
                  </div>
                  <p className="text-[hsl(var(--nearlab-gold-dark))] text-sm">Results usually back same day or next day</p>
                </div>
                <Button 
                  className="w-full bg-[hsl(var(--nearlab-gold))] text-[hsl(var(--nearlab-teal-dark))] hover:bg-[hsl(var(--nearlab-gold-dark))] font-semibold"
                  onClick={() => window.location.href = '/booking'}
                >
                  Schedule Non-DOT Test
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* SAMHSA Certified */}
      <section className="py-16 px-4 bg-[hsl(var(--nearlab-teal-light))/30]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-flex items-center gap-2 border border-[hsl(var(--nearlab-teal))] text-[hsl(var(--nearlab-teal))] px-4 py-1.5 rounded-full text-sm font-medium mb-4">
              <Shield className="h-4 w-4" />
              HHS / SAMHSA NLCP Certified
            </span>
            <h2 className="text-3xl font-bold text-[hsl(var(--nearlab-teal-dark))] mb-3">We Only Use HHS-Certified Laboratories</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Every specimen we collect is sent to a laboratory certified under the <strong>SAMHSA National Laboratory Certification Program (NLCP)</strong> — the federal standard administered by the Department of Health & Human Services. These labs are listed monthly in the Federal Register, undergo unannounced on-site inspections, and must pass rigorous performance testing to maintain certification. Your results are legally defensible and recognized by every DOT modal agency.
            </p>
          </div>
          <div className="bg-white border border-[hsl(var(--nearlab-teal))/20] rounded-xl p-6 max-w-3xl mx-auto text-center shadow-sm">
            <p className="text-[hsl(var(--nearlab-teal-dark))] text-sm font-semibold mb-2">What this means in practice</p>
            <p className="text-muted-foreground text-sm">
              The labs we work with — including nationally recognized facilities like <strong>LabCorp</strong> and <strong>Medtox</strong> — maintain SAMHSA NLCP certification, meaning they meet the same federal standards required for all DOT-regulated drug testing programs.
            </p>
          </div>
        </div>
      </section>

      {/* DOT Policy — 49 CFR Part 40 */}
      <section className="py-16 px-4 text-white" style={{ background: "var(--gradient-teal)" }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 bg-white/10 text-white/90 border border-white/20 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
              <Shield className="h-4 w-4" />
              Federal Regulation
            </span>
            <h2 className="text-3xl font-bold mb-3">Here's what DOT testing actually involves</h2>
            <p className="text-white/80 max-w-2xl mx-auto text-sm">
              The federal rules (<strong className="text-[hsl(var(--nearlab-gold))]">49 CFR Part 40</strong>) are designed 
              to keep safety-sensitive workers and the public protected. Here's what it means in plain terms.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {dotPolicySections.map((section, i) => (
              <div key={i} className="bg-white/10 border border-white/20 rounded-xl p-6 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-1">
                  <div className="bg-[hsl(var(--nearlab-gold))/20] p-2 rounded-lg">{section.icon}</div>
                  <h3 className="font-semibold text-lg text-white">{section.title}</h3>
                </div>
                <p className="text-white/50 text-xs mb-4 ml-12">Source: {section.source}</p>
                <ul className="space-y-2">
                  {section.points.map((point, j) => (
                    <li key={j} className="flex items-start gap-2 text-white/85 text-sm">
                      <CheckCircle className="h-4 w-4 text-[hsl(var(--nearlab-gold))] mt-0.5 shrink-0" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-10 bg-white/10 border border-white/20 rounded-xl p-6 text-center">
            <h3 className="font-semibold text-lg mb-2">Does this apply to your industry?</h3>
            <p className="text-white/70 text-sm mb-4">Under the Omnibus Transportation Employee Testing Act of 1991, DOT testing is mandatory across six modal agencies:</p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "FMCSA — Commercial Trucking & CDL",
                "FAA — Aviation",
                "FRA — Railroad",
                "FTA — Public Transit",
                "PHMSA — Pipeline & Hazardous Materials",
                "USCG — Maritime"
              ].map((agency) => (
                <span key={agency} className="bg-[hsl(var(--nearlab-gold))/15] text-[hsl(var(--nearlab-gold))] px-4 py-1.5 rounded-full text-sm font-medium border border-[hsl(var(--nearlab-gold))/30]">
                  {agency}
                </span>
              ))}
            </div>
            <p className="text-white/40 text-xs mt-5">
              Source: U.S. DOT Office of Drug & Alcohol Policy & Compliance (ODAPC) — <span className="underline">transportation.gov/odapc/part40</span> · 49 CFR Part 40
            </p>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-16 px-4 bg-background">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[hsl(var(--nearlab-teal-dark))] mb-4">It's simpler than you think</h2>
            <p className="text-muted-foreground">Most people are in and out in under 15 minutes. Here's how it goes:</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { num: "1", title: "Pick a time that works", body: "Book online in a few clicks — no phone tag, no waiting on hold" },
              { num: "2", title: "Come in, we handle the rest", body: "Our collector walks you through it — the whole thing takes about 10–15 minutes" },
              { num: "3", title: "Results sent securely", body: "You'll have your results within 24–48 hours, sent confidentially" },
            ].map((step) => (
              <div key={step.num} className="text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-[hsl(var(--nearlab-gold-light))] border-2 border-[hsl(var(--nearlab-gold))/40]">
                  <span className="text-2xl font-bold text-[hsl(var(--nearlab-teal-dark))]">{step.num}</span>
                </div>
                <h3 className="font-semibold text-[hsl(var(--nearlab-teal-dark))] mb-2">{step.title}</h3>
                <p className="text-muted-foreground">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-[hsl(var(--nearlab-teal-dark))] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to get it done?</h2>
          <p className="text-xl text-white/80 mb-8">We're here when you need us — no judgment, no hassle, just accurate results you can count on.</p>
          <Button 
            size="lg" 
            className="bg-[hsl(var(--nearlab-gold))] text-[hsl(var(--nearlab-teal-dark))] hover:bg-[hsl(var(--nearlab-gold-dark))] font-bold shadow-lg px-8"
            onClick={() => window.location.href = '/booking'}
          >
            Schedule Your Test Now
            <ArrowRight className="ml-2" size={18} />
          </Button>
        </div>
      </section>
      
      <SetmoreBooking />
    </div>
  </>;
};

export default DrugTesting;

